var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { Fragment, useState, useCallback, useEffect, createContext, useContext, } from "react";
import { isObservable, observable, isObservableArray, extendObservable, has as observableHas, isObservableObject, } from "mobx";
import { Submit } from "./Submit";
import { Button } from "./Button";
import { Alert } from "./Alert";
import { SectionHeader } from "./SectionHeader";
import parseChildren from "./utils/parseChildren";
import { get, set } from "lodash";
import { observer } from "mobx-react-lite";
import { useTranslation } from "./hooks/useTranslation";
import { useUtilityStyles } from "./hooks/useUtilityStyles";
var FormContext = createContext(undefined);
export function Form(_a) {
    var children = _a.children, values = _a.values, onSubmit = _a.onSubmit, disabled = _a.disabled, loading = _a.loading, _b = _a.type, type = _b === void 0 ? "raw" : _b, success = _a.success, error = _a.error, onCancel = _a.onCancel, _c = _a.title, title = _c === void 0 ? "" : _c, failure = _a.failure, _d = _a.isStatic, isStatic = _d === void 0 ? false : _d, id = _a.id, element = _a.element, styleOptions = _a.styleOptions;
    if (!isObservable(values)) {
        throw new Error("Form values must be observable");
    }
    return (React.createElement(FormContextProvider, { values: values, loading: loading, disabled: disabled, isStatic: isStatic },
        React.createElement(FormInternals, { onSubmit: onSubmit, type: type, success: success, error: error, onCancel: onCancel, title: title, failure: failure, id: id, element: element, styleOptions: styleOptions }, children)));
}
var FormAlerts = function (_a) {
    var _b = _a.success, success = _b === void 0 ? false : _b, form = _a.form, error = _a.error, _c = _a.failure, failure = _c === void 0 ? false : _c;
    var hasFailure;
    if (typeof failure == "boolean") {
        hasFailure = {
            text: "Oops. Something has gone wrong",
            show: failure,
        };
    }
    if (typeof failure == "string") {
        hasFailure = {
            text: failure,
            show: !!failure,
        };
    }
    return (React.createElement(Fragment, null,
        error && form === "edit" && (React.createElement(Alert, { type: "warning", message: error, glyph: "circleAlert" })),
        hasFailure && hasFailure.show && (React.createElement(Alert, { type: "error", message: hasFailure.text, glyph: "removeSign" })),
        success && form !== "raw" && !error && (React.createElement(Alert, { type: "success", message: "Success", glyph: "circleCheck", overPage: true }))));
};
export var useFormContext = function () {
    var context = useContext(FormContext);
    if (context === undefined) {
        throw new Error("Input does not have a form");
    }
    return context;
};
function injectedGet(key) {
    return get(this, key);
}
function injectedSet(key, value) {
    set(this, key, value);
}
export function useFormContextValues() {
    var context = useFormContext();
    return context.values;
}
function useFormValuesImpl(initialValues) {
    if (initialValues === void 0) { initialValues = {}; }
    var values = useState(function () {
        return observable.object(__assign(__assign({}, initialValues), { get: injectedGet, set: injectedSet }));
    })[0];
    return values;
}
/** Combines useState with a mobx object observable
 *
 * For use with Forms Elements that can find fields by name from
 * the returns values from this hook.
 *
 * @example
 * interface FormModel {
 *   firstName: string
 * }
 * // ...
 * const formValues = useTypedFormValues<FormModel>(initialFormValues);
 * // ...
 * <Input type="text" label="First Name" name="firstName" />
 */
export function useTypedFormValues(initialValues) {
    if (initialValues === void 0) { initialValues = {}; }
    return useFormValuesImpl(initialValues);
}
/** Do not use
 * @deprecated In favor of useTypedFormValues
 * @see useTypedFormValues
 */
export function useFormValues(initialValues) {
    if (initialValues === void 0) { initialValues = {}; }
    return useFormValuesImpl(initialValues);
}
export function FormContextProvider(_a) {
    var children = _a.children, values = _a.values, onSubmit = _a.onSubmit, disabled = _a.disabled, loading = _a.loading, isStatic = _a.isStatic;
    var _b = useState(false), isDirty = _b[0], setIsDirty = _b[1];
    var errors = useTypedFormValues();
    var formDisabled = disabled || loading;
    return (React.createElement(FormContext.Provider, { value: {
            values: values,
            onSubmit: onSubmit,
            errors: errors,
            formDisabled: formDisabled,
            isDirty: isDirty,
            setIsDirty: setIsDirty,
            arrayIndex: 0,
            isStatic: isStatic,
        } }, children));
}
function FormInternals(_a) {
    var children = _a.children, onSubmit = _a.onSubmit, _b = _a.type, type = _b === void 0 ? "raw" : _b, success = _a.success, error = _a.error, onCancel = _a.onCancel, _c = _a.title, title = _c === void 0 ? "" : _c, failure = _a.failure, id = _a.id, _d = _a.element, element = _d === void 0 ? "form" : _d, styleOptions = _a.styleOptions;
    var utilities = useUtilityStyles(styleOptions).utilities;
    var t = useTranslation();
    var _e = useFormContext(), formDisabled = _e.formDisabled, isDirty = _e.isDirty, setIsDirty = _e.setIsDirty, values = _e.values;
    var _f = useState("view"), form = _f[0], setForm = _f[1];
    var handleSubmit = useCallback(function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (onSubmit)
            onSubmit(values);
        setIsDirty(false);
    }, [onSubmit, values, setIsDirty]);
    useEffect(function () {
        if (success)
            setForm("view");
        if (error)
            setForm("edit");
    }, [success, error]);
    var resetForm = useCallback(function () {
        if (type === "detail") {
            setForm("view");
        }
        else if (type === "live") {
            setIsDirty(false);
        }
        if (onCancel)
            onCancel();
    }, [onCancel, type, setIsDirty]);
    var formChildren = parseChildren(children);
    var viewChildren = formChildren.findByType(FormView);
    var editChildren = formChildren.findByType(FormEdit);
    var FormHTMLElement = element !== null && element !== void 0 ? element : "form";
    return (React.createElement(FormHTMLElement, { autoComplete: element === "form" ? "off" : undefined, onSubmit: handleSubmit, id: id, className: utilities },
        type === "live" && (React.createElement(Fragment, null,
            React.createElement(SectionHeader, { title: title },
                isDirty && (React.createElement("div", { className: "inline-message" },
                    React.createElement("span", { className: "icon glyphicon-pro glyphicon-pro-circle-alert" }),
                    "There are changes on this form")),
                React.createElement(Button, { label: t("Revert Changes"), disabled: formDisabled || !isDirty, onClick: function () { return resetForm(); }, id: id && "".concat(id, "__revert") }),
                React.createElement(Submit, { inline: true, isDirty: isDirty, id: id && "".concat(id, "__submit") })),
            React.createElement(FormAlerts, { form: form, success: success, error: error, failure: failure }),
            editChildren || children)),
        type === "detail" && (React.createElement(Fragment, null,
            React.createElement(SectionHeader, { title: title },
                form === "edit" && (React.createElement(Fragment, null,
                    React.createElement(Button, { label: t("Cancel"), onClick: function () { return resetForm(); }, id: id && "".concat(id, "__cancel") }),
                    React.createElement(Submit, { inline: true, isDirty: isDirty, id: id && "".concat(id, "__submit") }))),
                form === "view" && (React.createElement(Button, { label: t("Edit"), type: "primary", onClick: function () { return setForm("edit"); }, id: id && "".concat(id, "__edit") }))),
            React.createElement(FormAlerts, { form: form, success: success, error: error, failure: failure }),
            form === "edit" && editChildren,
            form === "view" && viewChildren)),
        type === "raw" && (React.createElement(Fragment, null,
            React.createElement(FormAlerts, { form: form, success: success, error: error, failure: failure }),
            children))));
}
export var FormView = function (_a) {
    var children = _a.children;
    return React.createElement(Fragment, null, children);
};
export var FormEdit = function (_a) {
    var children = _a.children;
    return React.createElement(Fragment, null, children);
};
var ensureObservableProp = function (obj, key, template) {
    var _a;
    if (!observableHas(obj, key)) {
        extendObservable(obj, (_a = {}, _a[key] = template, _a));
    }
};
var useEnsureObservableProp = function (values, name, defaultValue) {
    var _a = React.useState(true), loading = _a[0], setLoading = _a[1];
    useEffect(function () {
        ensureObservableProp(values, name, defaultValue);
        setLoading(false);
    }, [values, name, loading, setLoading, defaultValue]);
    return loading;
};
var injectLodashWorkaround = function (value) {
    // workaround for lodash access method
    value.get = injectedGet;
    value.set = injectedSet;
};
var ERROR_ID_COUNTER = 0;
export var FormObject = function (_a) {
    var children = _a.children, name = _a.name;
    var _b = useFormContext(), parentValues = _b.values, parentErrors = _b.errors, arrayIndex = _b.arrayIndex, parentContext = __rest(_b, ["values", "errors", "arrayIndex"]);
    if (useEnsureObservableProp(parentValues, name, {}))
        return null;
    var values = parentValues[name];
    if (!isObservableObject(values)) {
        throw new Error("FormObject values must be observable object");
    }
    injectLodashWorkaround(values);
    return (React.createElement(FormElement, { arrayIndex: arrayIndex, values: values, parentErrors: parentErrors, parentContext: parentContext }, children));
};
export var FormArray = observer(function (_a) {
    var children = _a.children, name = _a.name;
    var _b = useFormContext(), parentValues = _b.values, parentErrors = _b.errors, _ = _b.arrayIndex, parentContext = __rest(_b, ["values", "errors", "arrayIndex"]);
    if (useEnsureObservableProp(parentValues, name, []))
        return null;
    var entries = parentValues[name];
    if (!isObservableArray(entries)) {
        throw new Error("FormArray values must be observable array");
    }
    return (React.createElement(Fragment, null, entries.map(function (values, i) {
        return (React.createElement(FormElement, { key: i, arrayIndex: i, values: values, parentErrors: parentErrors, parentContext: parentContext }, children));
    })));
});
export var FormElement = function (_a) {
    var children = _a.children, values = _a.values, arrayIndex = _a.arrayIndex, parentContext = _a.parentContext, parentErrors = _a.parentErrors;
    var errorId = React.useState(function () { return "sf".concat(ERROR_ID_COUNTER++); })[0];
    if (useEnsureObservableProp(parentErrors, errorId, {}))
        return null;
    var errors = parentErrors[errorId];
    injectLodashWorkaround(values);
    return (React.createElement(FormContext.Provider, { value: __assign(__assign({}, parentContext), { values: values, errors: errors, arrayIndex: arrayIndex }) }, children));
};
export var FormConsumer = function (_a) {
    var children = _a.children;
    var context = useFormContext();
    return children(context);
};
export var FormActions = function (_a) {
    var children = _a.children;
    return React.createElement("div", { className: "form--actions" }, children);
};
